import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from 'i18next';

import { useKeycloakContext } from '@/contexts/keycloak/context';
import { useUserContext } from '@/contexts/user/context';
import {
  SSO_LOGIN_DOMAIN_KEY,
  SSO_LOGIN_ERROR_KEY,
  SSO_PENDING_KEY,
  STATUS_PENDING,
} from '@/assets/AppConstants';

import {
  checkSsoDomain,
  createSession,
  listAvailableClients,
  selectClient,
  verifyMfaToken,
} from '../api';
import { describeDevice } from '../lib/device';
import { toErrorMessage } from '../lib/errors';
import type {
  AvailableClient,
  Credentials,
  LoginStep,
  MfaChallenge,
  Session,
} from '../model/types';

interface SsoSession {
  external?: boolean;
  realmId?: string | null;
  authHost?: string | null;
  authenticated?: boolean;
}

interface UserContextBridge {
  handleSessionEstablished?: (session: SsoSession & Session) => void;
  setUserSession?: (session: SsoSession & Session) => void;
  logoutUser?: () => void;
  userSession?: SsoSession;
  ssoChallenge?: MfaChallenge | null;
  clearSsoChallenge?: () => void;
}

let ssoErrorFlaggedThisLoad = false;

const readSavedLoginDomain = () => localStorage.getItem(SSO_LOGIN_DOMAIN_KEY) ?? '';

const isSsoHandshakeInFlight = (userContext?: UserContextBridge) => {
  const session = userContext?.userSession;
  return Boolean(
    session?.external
    && session.realmId
    && session.authHost
    && !session.authenticated
    && !userContext?.ssoChallenge,
  );
};

const AUTO_LOGIN = import.meta.env.DEV
  && import.meta.env.VITE_AUTO_LOGIN_EMAIL
  && import.meta.env.VITE_AUTO_LOGIN_PASSWORD
  ? {
    username: String(import.meta.env.VITE_AUTO_LOGIN_EMAIL),
    password: String(import.meta.env.VITE_AUTO_LOGIN_PASSWORD),
  }
  : null;

// Depth per step, so a transition can animate in the direction the user moved.
const DEPTH: Record<LoginStep, number> = { credentials: 0, sso: 1, mfa: 1, client: 2 };

export function useLoginFlow() {
  const keycloak = useKeycloakContext() as { loginKeycloak?: () => void } | undefined;
  const userContext = useUserContext() as UserContextBridge | undefined;

  const [savedLoginDomain] = useState(readSavedLoginDomain);
  const initialStep: LoginStep = savedLoginDomain ? 'sso' : 'credentials';

  const [step, setStepState] = useState<LoginStep>(initialStep);
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [ssoStarted, setSsoStarted] = useState(false);

  const stepRef = useRef<LoginStep>(initialStep);

  const setStep = useCallback((next: LoginStep) => {
    setDirection(DEPTH[next] < DEPTH[stepRef.current] ? 'back' : 'forward');
    stepRef.current = next;
    setStepState(next);
  }, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  const [clients, setClients] = useState<AvailableClient[]>([]);
  const [unverified, setUnverified] = useState<Session | null>(null);

  useEffect(() => {
    if (localStorage.getItem(SSO_LOGIN_ERROR_KEY)) {
      localStorage.removeItem(SSO_LOGIN_ERROR_KEY);
      ssoErrorFlaggedThisLoad = true;
    }
    if (!ssoErrorFlaggedThisLoad) return;
    setError(t('We could not sign you in with your corporate account. Please contact your organization administrator.'));
  }, []);

  const commit = useCallback((session: Session) => {
    if (session.status === STATUS_PENDING) {
      setUnverified(session);
      return;
    }
    const ssoSession = userContext?.userSession;
    const isSso = Boolean(ssoSession?.external && userContext?.ssoChallenge);
    userContext?.handleSessionEstablished?.(
      isSso ? { ...ssoSession, ...session, external: true, authenticated: true } : session,
    );
    if (isSso) userContext?.clearSsoChallenge?.();
  }, [userContext]);

  const loadClients = useCallback(async () => {
    try {
      setClients(await listAvailableClients());
      setStep('client');
    } catch (cause) {
      setError(toErrorMessage(cause, t('We could not load your accounts.')));
    }
  }, [setStep]);

  const run = useCallback(async <T>(action: () => Promise<T>, fallback: string) => {
    setBusy(true);
    ssoErrorFlaggedThisLoad = false;
    setError(null);
    try {
      return await action();
    } catch (cause) {
      setError(toErrorMessage(cause, fallback));
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  const submitCredentials = useCallback(async (credentials: Credentials) => {
    const session = await run(
      () => createSession(credentials),
      t('We could not sign you in. Check your email and password.'),
    );
    if (!session) return;

    if (session.clientId) {
      commit(session);
      return;
    }

    setChallenge({ qrCode: session.qrCode ?? null, needsToken: Boolean(session.needsToken) });
    if (session.needsToken) {
      setStep('mfa');
    } else {
      await loadClients();
    }
  }, [commit, loadClients, run, setStep]);

  const submitMfaToken = useCallback(async (mfaToken: string, trustDevice: boolean) => {
    const session = await run(
      () => verifyMfaToken({
        token: mfaToken,
        ...(trustDevice ? { device: describeDevice() } : {}),
      }),
      t('That code is not valid. Please try again.'),
    );
    if (!session) return;

    if (session.clientId) {
      commit(session);
      return;
    }
    await loadClients();
  }, [commit, loadClients, run]);

  const submitClient = useCallback(async (client: AvailableClient) => {
    const session = await run(
      () => selectClient(String(client.id)),
      t('We could not open that account.'),
    );
    if (!session) return;
    commit({ ...session, clientName: client.company, multiClient: true });
  }, [commit, run]);

  const submitSsoDomain = useCallback(async (loginDomain: string) => {
    sessionStorage.setItem(SSO_PENDING_KEY, '1');
    const session = await run(
      () => checkSsoDomain(loginDomain),
      t('We could not reach that organization.'),
    );
    if (!session) {
      sessionStorage.removeItem(SSO_PENDING_KEY);
      return;
    }

    if (session.external) {
      localStorage.setItem(SSO_LOGIN_DOMAIN_KEY, loginDomain);
      userContext?.setUserSession?.({ ...userContext.userSession, ...session, realmId: session.clientId });
      setSsoStarted(true);
      keycloak?.loginKeycloak?.();
    } else {
      sessionStorage.removeItem(SSO_PENDING_KEY);
      setError(t('You have no an external account enabled'));
    }
  }, [keycloak, run, userContext]);

  const cancelSso = useCallback(() => {
    sessionStorage.removeItem(SSO_PENDING_KEY);
    setSsoStarted(false);
    userContext?.logoutUser?.();
  }, [userContext]);

  const ssoChallengeHandled = useRef(false);
  const ssoChallenge = userContext?.ssoChallenge;
  useEffect(() => {
    if (!ssoChallenge || ssoChallengeHandled.current) return;
    ssoChallengeHandled.current = true;
    setError(null);
    setChallenge(ssoChallenge);
    if (ssoChallenge.needsToken) {
      setStep('mfa');
    } else {
      loadClients();
    }
  }, [ssoChallenge, loadClients, setStep]);

  const autoLoginFired = useRef(false);
  useEffect(() => {
    if (!AUTO_LOGIN || autoLoginFired.current) return;
    autoLoginFired.current = true;
    submitCredentials(AUTO_LOGIN);
  }, [submitCredentials]);

  const goTo = useCallback((next: LoginStep) => {
    ssoErrorFlaggedThisLoad = false;
    setError(null);
    setStep(next);
  }, [setStep]);

  return {
    step,
    direction,
    busy,
    error,
    challenge,
    clients,
    unverified,
    goTo,
    clearUnverified: () => setUnverified(null),
    dismissError: () => { ssoErrorFlaggedThisLoad = false; setError(null); },
    autoLogin: Boolean(AUTO_LOGIN),
    savedLoginDomain,
    ssoRedirecting: ssoStarted || isSsoHandshakeInFlight(userContext),
    cancelSso,
    submitCredentials,
    submitMfaToken,
    submitClient,
    submitSsoDomain,
  };
}
